import { AdjustmentsHorizontalIcon, FolderIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { pick, transform, uniq } from "lodash-es";
import { useEffect, useLayoutEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { favoriteFiltersListName, ListFieldsType, myDocumentsListName } from "src/api/config";
import { elasticSearchByText, elasticSearchByTitle } from "src/api/elasticsearch";
import { useFiltersContext } from "src/contexts/FilterContext";
import { useFoldersContext } from "src/contexts/FolderContext";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { modifiedMyDocumentsList } from "src/modules/acadia/my_documents/selectors";
import { getItemsRequest } from "src/modules/spItems/api";
import {
  modifiedItemsState,
  selectAddStatus,
  selectDeleteStatus,
  selectGetStatus,
  selectItems,
  selectUpdateStatus,
} from "src/modules/spItems/selectors";
import { MyDocumentsType, UserListItem } from "src/types/main";
import { getLocalItem, setLocalItem } from "src/utils/localStorage";
import { buildDocumentSearchFilter, isCodeIndexQuery } from "src/utils/searchFilter.utils";
import { searchAndHighlight } from "src/utils/searchHighlight";
import { twMerge } from "tailwind-merge";
import TableWithFilters from "../../../components/tables/TableWithFilters.mobile";
import { SearchUI } from "../../../components/ui/Search";
import { FlexCol, FlexRow } from "../../../utils/flexLayouts";
import CustomCheckbox from "./components/Tests/components/Ui/CustomCheckbox";
import TGButton from "./../../../components/ui/TGButton";
import DocumentFilters from "./components/Filters/DocumentFilters";
import Folders from "./components/Folders";
import { keyMap } from "./data/keyMap";
import { FilterType, Keys, KeysList, RestKeys, UserKeys } from "./type";
import { addUsers } from "src/modules/users/actions";
import _ from "lodash";
import FavoriteButton from "./components/FavoriteButton";
import { addItem, deleteItem, getItems, updateItem } from "src/modules/spItems/actions";
import DocFavoriteFiltersManager, { isDocFiltersEmpty } from "./components/favorites/DocFavoriteFiltersManager";
import FavoriteFiltersModal from "src/pages/Reports/WorkPlaceReport/components/FiltersModal";
import DialogComponent from "src/components/DialogComponent";
import InputFilterSet from "src/components/InputFilterSet";
import { ChevronIcon, SaveIcon } from "src/pages/Reports/shared/icons";
import Loading from "src/components/Loading";
import { selectCurrentUser } from "src/modules/users/selector";

const searchPaths = [
  "code.code",
  "code.name",
];

const EMPTY_SEARCH_META = {
  foundBy: "title" as const,
  highlightedText: "",
  elasticsearchId: "",
  elasticsearchScore: 0,
  DocumentFileId: null,
};

const withElasticFallback = async (
  operation: () => Promise<{ results: MyDocumentsType[]; hasMore: boolean; lastFrom: number }>,
): Promise<{ results: MyDocumentsType[]; hasMore: boolean; lastFrom: number }> => {
  try {
    return await operation();
  } catch (error) {
    console.error("[MyDocumentPageMobile] Elasticsearch request failed (silent fallback to empty):", error);
    return {
      results: [],
      hasMore: false,
      lastFrom: 0,
    };
  }
};

function mergeSearchResults(
  spResults: MyDocumentsType[],
  esTitleResults: MyDocumentsType[],
  esTextResults: MyDocumentsType[],
): MyDocumentsType[] {
  const esMap = new Map<number, MyDocumentsType>();

  for (const doc of esTitleResults) {
    esMap.set(doc.id, doc);
  }

  for (const doc of esTextResults) {
    const existing = esMap.get(doc.id);
    if (existing) {
      esMap.set(doc.id, { ...doc, searchMeta: { ...doc.searchMeta!, foundBy: "both" } });
    } else {
      esMap.set(doc.id, doc);
    }
  }

  const spIds = new Set(spResults.map((doc) => doc.id));
  const mergedSp = spResults.map((doc) => {
    const esMatch = esMap.get(doc.id);
    if (esMatch?.searchMeta) return { ...doc, searchMeta: esMatch.searchMeta };
    return { ...doc, searchMeta: EMPTY_SEARCH_META };
  });

  const esOnly = [...esMap.values()].filter((doc) => !spIds.has(doc.id));
  const esOnlyTitle = esOnly.filter((doc) => doc.searchMeta?.foundBy !== "content");
  const esOnlyContent = esOnly.filter((doc) => doc.searchMeta?.foundBy === "content");

  return [...mergedSp, ...esOnlyTitle, ...esOnlyContent];
}

const MyDocumentPage = () => {
  const { folders, fromFilterToFolders, clearFolders } = useFoldersContext();
  const { filters, fromFolderToFilters, statusFilters } = useFiltersContext();
  const [activeFilterType, setActiveFilterType] = useState<FilterType>(getLocalItem("filter_active") || "folder");
  const [isShowFilterOrDirectory, setIsShowFilterOrDirectory] = useState<boolean>(false);
  const [hasNonEmptyArray, setHasNonEmptyArray] = useState(false);
  const favoriteFiltersData = useAppSelector((state) => selectItems(state, favoriteFiltersListName));
  const favoriteFiltersGetStatus = useAppSelector((state) => selectGetStatus(state, favoriteFiltersListName));
  const favoriteFiltersUpdatedStatus = useAppSelector((state) => selectUpdateStatus(state, favoriteFiltersListName));
  const favoriteFiltersDeletedStatus = useAppSelector((state) => selectDeleteStatus(state, favoriteFiltersListName));
  const favoriteFiltersAddedStatus = useAppSelector((state) => selectAddStatus(state, favoriteFiltersListName));


  // Favorites state
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentFavoriteId, setCurrentFavoriteId] = useState<string | null>(null);
  const [bundlesModalOpened, setBundlesModalOpened] = useState(false);
  const [favoriteFilterModalOpened, setFavoriteFilterModalOpened] = useState(false);
  const [filtersReady, setFiltersReady] = useState(() => !isDocFiltersEmpty(filters));

  const user = useAppSelector((state) => selectCurrentUser(state));
  const dispatch = useAppDispatch();

  function toggleFilter(type: FilterType) {
    setIsShowFilterOrDirectory(true);

    if (type === "filter") {
      if (!statusFilters) {
        fromFolderToFilters(folders);
      }
    }

    if (type === "folder") {
      if (!statusFilters) {
        fromFilterToFolders(filters);
      } else {
        clearFolders();
      }
    }

    setLocalItem("filter_active", type);
    setActiveFilterType(type);
  }

  if (!getLocalItem("filter_active")) {
    setLocalItem("filter_active", "folder");
  }

  useEffect(() => {
    setHasNonEmptyArray(
      _.some(
        filters,
        (value) => (_.isArray(value) && !_.isEmpty(value)) || filters.dateForRevision.required || !!filters.dateForRevision.date.start,
      ),
    );
  }, [JSON.stringify(filters)]);

  const hasNonEmptyFolders = useMemo(() => {
    return folders?.collections != null;
  }, [folders]);

  const loadingStatus =
    favoriteFiltersUpdatedStatus === "LOADING" ||
    favoriteFiltersDeletedStatus === "LOADING" ||
    favoriteFiltersAddedStatus === "LOADING" ||
    favoriteFiltersGetStatus === "LOADING";


  const deleteCurrentFilter = () => {
    if (currentFavoriteId) {
      dispatch(deleteItem({ listName: favoriteFiltersListName, ID: currentFavoriteId }));
      setIsFavorite(false);
      setCurrentFavoriteId(null);
    }
  };

  const handleToggleFavorite = () => {
    if (isFavorite) {
      deleteCurrentFilter(); // onDeleteFilter();
    } else {
      setFavoriteFilterModalOpened(true); // onSaveFilter();
    }
    setBundlesModalOpened(false);
  };

  const saveFilter = ({ name, isDefault }: { name: string; isDefault: boolean }) => {
    if (isDefault) {
      favoriteFiltersData
        .filter((item) => item.isDefault && item.Type === "doc")
        .forEach((item) => {
          dispatch(updateItem({ listName: favoriteFiltersListName, body: { ID: item.ID, isDefault: false } }));
        });
    }

    dispatch(
      addItem({
        listName: favoriteFiltersListName,
        body: {
          Title: name,
          DocumentsFilter: JSON.stringify(filters),
          Type: "doc",
          isDefault: isDefault,
        },
      }),
    );
    setFavoriteFilterModalOpened(false);
  };

  useEffect(() => {
    if (favoriteFiltersDeletedStatus === "SUCCESS" && user?.id) {
      dispatch(getItems({
        listName: favoriteFiltersListName,
        filters: { Author: user.id, Type: "doc" }
      }));
    }
  }, [favoriteFiltersDeletedStatus, user?.id]);

  return (
    <FlexCol>
      {isShowFilterOrDirectory && (
        <FlexRow className="py-5 h-[76px] border-b items-center justify-between">
          {activeFilterType === "folder" ? "Папки" : "Фильтры"}

          <FlexRow className="gap-[5px] items-center">
            {activeFilterType !== "folder" && (
              <FavoriteButton
                isFavorite={{ state: isFavorite, id: null }}
                onToggle={handleToggleFavorite}
                disabled={loadingStatus}
              />
            )}

            {loadingStatus ? (
              <Loading />
            ) : favoriteFiltersData.length > 0 ? (
              <button onClick={() => setBundlesModalOpened((prev) => !prev)}>
                <ChevronIcon className="w-5 h-3 duration-300 rotate-180 text-dark6 hover:text-darkblue" />
              </button>
            ) : null}

            <button type="button" onClick={() => {
              setIsShowFilterOrDirectory(false);
              setBundlesModalOpened(false);
            }}>
              <span className="sr-only">Close</span>
              <XMarkIcon aria-hidden="true" className="h-6 w-6" />
            </button>
          </FlexRow>
        </FlexRow>
      )}

      <FlexCol className="w-full">
        <div style={{ display: isShowFilterOrDirectory ? "block" : "none" }}>
          <div style={{ display: activeFilterType === "folder" ? "block" : "none" }}>
            <Folders />
          </div>
          <div style={{ display: activeFilterType === "filter" ? "block" : "none" }}>
            <DocumentFilters />
          </div>
        </div>

        <DocFavoriteFiltersManager
          isFavorite={isFavorite}
          setIsFavorite={setIsFavorite}
          currentFavoriteId={currentFavoriteId}
          setCurrentFavoriteId={setCurrentFavoriteId}
          bundlesModalOpened={bundlesModalOpened && activeFilterType === "filter"}
          setBundlesModalOpened={setBundlesModalOpened}
          setFiltersReady={setFiltersReady}
        />

        <div style={{ display: !isShowFilterOrDirectory ? "block" : "none" }}>
          <ContentArea
            activeFilterType={activeFilterType}
            toggleFilter={toggleFilter}
            hasNonEmptyArray={hasNonEmptyArray}
            hasNonEmptyFolders={hasNonEmptyFolders}
            saveFilter={saveFilter}
            currentFavoriteId={currentFavoriteId}
            setCurrentFavoriteId={setCurrentFavoriteId}
            favoriteFilterModalOpened={favoriteFilterModalOpened}
            setFavoriteFilterModalOpened={setFavoriteFilterModalOpened}
            setIsFavorite={setIsFavorite}
            isFavorite={isFavorite}
            bundlesModalOpened={bundlesModalOpened}
            setBundlesModalOpened={setBundlesModalOpened}
            filtersReady={filtersReady}
            setFiltersReady={setFiltersReady}
          />
        </div>
      </FlexCol>
    </FlexCol>
  );
};

function ContentArea({
  activeFilterType,
  toggleFilter,
  hasNonEmptyArray,
  hasNonEmptyFolders,
  saveFilter,
  currentFavoriteId,
  favoriteFilterModalOpened,
  setFavoriteFilterModalOpened,
  filtersReady,
}: {
  activeFilterType: "folder" | "filter";
  toggleFilter: (type: FilterType) => void;
  hasNonEmptyArray: boolean,
  hasNonEmptyFolders: boolean,
  saveFilter: ({ name, isDefault }: { name: string; isDefault: boolean }) => void,
  currentFavoriteId: string | null,
  setCurrentFavoriteId: (id: string | null) => void,
  favoriteFilterModalOpened: boolean,
  setFavoriteFilterModalOpened: (v: boolean) => void,
  isFavorite: boolean,
  setIsFavorite: (v: boolean) => void,
  bundlesModalOpened: boolean,
  setBundlesModalOpened: (v: boolean) => void,
  filtersReady: boolean,
  setFiltersReady: (v: boolean) => void
}) {
  const [loading, setLoading] = useState(false);
  const [nextPage, setNextPage] = useState<any>(null);
  const dispatch = useAppDispatch();
  const { filters } = useFiltersContext();
  const { folders, isFoldersEmpty, fromFilterToFolders } = useFoldersContext();

  const foldersSyncedOnceRef = useRef(false);

  const [documents, setDocuments] = useState<MyDocumentsType[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchByPartialMatch, setSearchByPartialMatch] = useState(false);
  const [searchByContent, setSearchByContent] = useState(false);
  const [page, setPage] = useState(1);
  const [esTitleFrom, setEsTitleFrom] = useState(0);
  const [esTextFrom, setEsTextFrom] = useState(0);
  const [hasMoreEsTitle, setHasMoreEsTitle] = useState(false);
  const [hasMoreEsText, setHasMoreEsText] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [filterTitle, setIsFilterSetTitle] = useState("");

  function updateSetFilter() {
    if (currentFavoriteId) {
      dispatch(updateItem({ listName: favoriteFiltersListName, body: { ID: currentFavoriteId, Title: filterTitle } }));
    }

    closeDialog();
  }

  function closeDialog() {
    setIsSaved(false);
  }

  const getSetTitle = useCallback((title: string) => {
    setIsFilterSetTitle(title);
  }, []) as (title: string) => void;



  function cb() {
    setPage((prev) => prev + 1);
  }

  function handleSearch(value: string) {
    setSearchQuery(value);
  }

  const handleToggleSearchByPartialMatch = () => {
    setSearchByPartialMatch((previousValue) => !previousValue);
  };

  const handleToggleSearchByContent = () => {
    setSearchByContent((prev) => !prev);
  };

  useLayoutEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
    setLoading(true);
    setEsTitleFrom(0);
    setEsTextFrom(0);
    setHasMoreEsTitle(false);
    setHasMoreEsText(false);
  }, [JSON.stringify(filters), JSON.stringify(folders)]);

  useLayoutEffect(() => {
    setPage(1);
    setLoading(true);
    setEsTitleFrom(0);
    setEsTextFrom(0);
    setHasMoreEsTitle(false);
    setHasMoreEsText(false);
  }, [searchQuery, searchByPartialMatch, searchByContent]);

  useEffect(() => {
    if (!filtersReady) return;
    if (activeFilterType !== "folder") return;
    if (foldersSyncedOnceRef.current) return;
    if (!isDocFiltersEmpty(filters)) {
      fromFilterToFolders(filters as any);
    }
    foldersSyncedOnceRef.current = true;
  }, [filtersReady, activeFilterType, filters, fromFilterToFolders]);

  function modified(data: ListFieldsType[typeof myDocumentsListName][]) {
    const myDocuments = modifiedMyDocumentsList(data);
    return searchAndHighlight(myDocuments, searchQuery || "", searchPaths);
  }

  useEffect(() => {
    const hasSearch = searchQuery.length > 0;

    if (!hasSearch) {
      if (activeFilterType === "filter") {
        if (!filtersReady || isDocFiltersEmpty(filters)) {
          setDocuments([]);
          setLoading(false);
          return;
        }
      } else {
        if (!isFoldersEmpty()) {
          setDocuments([]);
          setLoading(false);
          return;
        }
      }
    }

    let cancelled = false;

    async function fetchData() {
      setLoading(true);

      if (page === 1) {
        setDocuments([]);
        setNextPage(null);
      }

      try {
        let baseFilters = pick(filters, ["collections", "departments", "sectors", "processes", "types"]);
        let restFilters = pick(filters, [
          "critical",
          "lines",
          "sap",
          "typeEquipment",
          "model",
          "manufacturer",
          "titleEquipment",
          "workstations",
        ]);
        let userFilters = pick(filters, ["created", "responsibleForRevision"]);
        let dateFilters = pick(filters, ["dateForRevision"]);

        const configureBaseFilters: Record<Keys, number[]> =
          activeFilterType === "filter"
            ? transform(baseFilters, (result: any, value: any[], key: string) => {
                let newKey = keyMap[key as KeysList];
                let newValue = value.map((v: { id: any }) => v.id);

                if (newValue.length > 0) {
                  (result as any)[newKey] = newValue;
                }
              })
            : transform(folders, (result: any, value: any, key: string) => {
                let newKey = keyMap[key as KeysList];

                if (value) {
                  (result as any)[newKey] = value.id;
                }
              });

        const userFiltersData = Object.keys(userFilters).reduce((acc, key) => {
          const newKey = keyMap[key as UserKeys];

          if (userFilters[key as UserKeys].length > 0) {
            const ids = userFilters[key as UserKeys].map((r: UserListItem) => r.email.toLowerCase());
            acc[newKey] = ids;
          }

          return acc;
        }, {} as Record<(typeof keyMap)[UserKeys], string[]>);

        const allFilters = Object.keys(restFilters).reduce(
          (acc, key) => {
            const newValue = restFilters[key as RestKeys];
            const newKey = keyMap[key as RestKeys];

            if (key === "critical") {
              if (newValue.length > 0 && newValue.length < 2) {
                acc[newKey] = Boolean(newValue[0].id);
              }
            } else if (newValue.length > 0) {
              const arrayIds = newValue.map((r: { id: any }) => r.id);
              if (acc[newKey]) {
                acc[newKey] = uniq([...acc[newKey], ...arrayIds]);
              } else {
                acc[newKey] = newValue.map((r: { id: any }) => r.id);
              }
            }

            return acc;
          },
          { ...configureBaseFilters, ...userFiltersData, Status: [8, 3] } as Record<string, any>,
        );

        if (
          dateFilters.dateForRevision.required ||
          dateFilters.dateForRevision.date.start ||
          dateFilters.dateForRevision.date.end
        ) {
          let dateFiltersData: { start: Date | null; end: Date | null }[] = [];
          const newKey = keyMap["dateForRevision"];

          if (dateFilters.dateForRevision.required) {
            allFilters["Status"] = [3];
          }

          if (dateFilters.dateForRevision.date.start || dateFilters.dateForRevision.date.end) {
            dateFiltersData.push({
              start:
                typeof dateFilters.dateForRevision.date.start === "string"
                  ? new Date(dateFilters.dateForRevision.date.start)
                  : dateFilters.dateForRevision.date.start,
              end:
                typeof dateFilters.dateForRevision.date.end === "string"
                  ? new Date(dateFilters.dateForRevision.date.end)
                  : dateFilters.dateForRevision.date.end,
            });

            allFilters[newKey] = dateFiltersData;
          }
        }

        if (searchQuery.length > 0) {
          const usePartialMatchSearch = searchByPartialMatch || searchByContent;
          Object.assign(allFilters, buildDocumentSearchFilter(searchQuery, usePartialMatchSearch));
          const isCodeSearch = isCodeIndexQuery(searchQuery);

          const filterElasticResults = (results: MyDocumentsType[]): MyDocumentsType[] => {
            return results.filter((doc) => {
              if (activeFilterType === "folder") {
                if (folders.collections && doc.aboutDocument.collection !== folders.collections.title) return false;
                if (folders.departments && doc.aboutDocument.department !== folders.departments.title) return false;
                if (folders.sectors && doc.aboutDocument.sector !== folders.sectors.title) return false;
                if (folders.processes && doc.aboutDocument.process !== folders.processes.title) return false;
                if (folders.types && doc.aboutDocument.type !== folders.types.title) return false;
              } else {
                if (
                  filters.collections.length > 0 &&
                  !filters.collections.some((filter) => filter.title === doc.aboutDocument.collection)
                )
                  return false;
                if (
                  filters.departments.length > 0 &&
                  !filters.departments.some((filter) => filter.title === doc.aboutDocument.department)
                )
                  return false;
                if (
                  filters.sectors.length > 0 &&
                  !filters.sectors.some((filter) => filter.title === doc.aboutDocument.sector)
                )
                  return false;
                if (
                  filters.processes.length > 0 &&
                  !filters.processes.some((filter) => filter.title === doc.aboutDocument.process)
                )
                  return false;
                if (filters.types.length > 0 && !filters.types.some((filter) => filter.title === doc.aboutDocument.type))
                  return false;
              }

              if (filters.critical.length > 0 && filters.critical.length < 2) {
                const isCritical = Boolean(filters.critical[0].id);
                if (doc.critical !== isCritical) return false;
              }

              if (filters.responsibleForRevision.length > 0) {
                const emails = filters.responsibleForRevision.map((person) => person.email.toLowerCase());
                if (!emails.includes(doc.responsibleForRevision.email.toLowerCase())) return false;
              }

              return true;
            });
          };

          const fetchElasticResultsWithFallback = async (
            searchFn: (query: string, from?: number) => Promise<MyDocumentsType[]>,
            startFrom: number,
            rowLimit: number = 100,
            minFilteredResults: number = 50,
            onFilteredChunk?: (chunk: MyDocumentsType[]) => void,
          ): Promise<{ results: MyDocumentsType[]; hasMore: boolean; lastFrom: number }> => {
            const maxRequests = 20;
            let currentFrom = startFrom;
            let allResults: MyDocumentsType[] = [];
            let hasMore = true;
            let lastFrom = startFrom;
            let requestsCount = 0;

            while (hasMore && allResults.length < minFilteredResults && requestsCount < maxRequests) {
              const chunk = await searchFn(searchQuery, currentFrom);
              requestsCount += 1;
              lastFrom = currentFrom;

              if (!chunk.length) {
                hasMore = false;
                continue;
              }

              const filtered = filterElasticResults(chunk);
              if (filtered.length > 0) {
                allResults = [...allResults, ...filtered];
                onFilteredChunk?.(filtered);
              }

              currentFrom += rowLimit;
            }

            if (requestsCount >= maxRequests && allResults.length < minFilteredResults) {
              hasMore = false;
            }

            return { results: allResults, hasMore, lastFrom };
          };

          const [spResponse, esTitleResponse] = await Promise.all([
            page === 1
              ? getItemsRequest({
                  listName: myDocumentsListName,
                  filters: allFilters,
                  sortOrder: { name: "Created", desc: true },
                  rowLimit: 10,
                })
              : nextPage
                ? getItemsRequest({
                    listName: myDocumentsListName,
                    filters: allFilters,
                    sortOrder: { name: "Created", desc: true },
                    rowLimit: 10,
                    nextPage,
                  })
                : Promise.resolve({ items: [] as any[], nextPage: null }),
            isCodeSearch || !usePartialMatchSearch
              ? Promise.resolve({ results: [] as MyDocumentsType[], hasMore: false, lastFrom: 0 })
              : page === 1
                ? withElasticFallback(() => fetchElasticResultsWithFallback(elasticSearchByTitle, 0))
                : hasMoreEsTitle
                  ? withElasticFallback(() => fetchElasticResultsWithFallback(elasticSearchByTitle, esTitleFrom))
                  : Promise.resolve({ results: [] as MyDocumentsType[], hasMore: false, lastFrom: esTitleFrom }),
          ]);

          if (cancelled) return;

          const spResults = modified(modifiedItemsState(spResponse.items, myDocumentsListName));
          const phase1Docs = mergeSearchResults(spResults, esTitleResponse.results, []);

          setNextPage(spResponse.nextPage ?? null);
          setHasMoreEsTitle(esTitleResponse.hasMore);
          if (esTitleResponse.hasMore) setEsTitleFrom(esTitleResponse.lastFrom + 100);
          else if (page === 1) setEsTitleFrom(0);

          if (page === 1) {
            setDocuments(phase1Docs);
          } else {
            setDocuments((prev) => {
              const existingIds = new Set(prev.map((doc) => doc.id));
              return [...prev, ...phase1Docs.filter((doc) => !existingIds.has(doc.id))];
            });
          }

          if (!isCodeSearch && searchByContent && (page === 1 || hasMoreEsText)) {
            const textPageSize = 25;
            const startFrom = page === 1 ? 0 : esTextFrom;
            const esTextResponse = await withElasticFallback(() =>
              fetchElasticResultsWithFallback(
                (query, from) => elasticSearchByText(query, from, textPageSize),
                startFrom,
                textPageSize,
                textPageSize,
                (chunk) => {
                  if (cancelled) return;
                  setDocuments((prev) => {
                    const existingIds = new Set(prev.map((doc) => doc.id));
                    return [...prev, ...chunk.filter((doc) => !existingIds.has(doc.id))];
                  });
                },
              ),
            );

            if (cancelled) return;

            setHasMoreEsText(esTextResponse.hasMore);
            if (esTextResponse.hasMore) setEsTextFrom(esTextResponse.lastFrom + textPageSize);
            else if (page === 1) setEsTextFrom(0);
          } else if (page === 1) {
            setHasMoreEsText(false);
            setEsTextFrom(0);
          }

          return;
        }

        if (page === 1) {
          const myDocsResponse = await getItemsRequest({
            listName: myDocumentsListName,
            filters: allFilters,
            sortOrder: { name: "Created", desc: true },
            rowLimit: 10,
          });

          if (cancelled) return;
          setNextPage(myDocsResponse.nextPage);

          const modifiedmyDocs = modifiedItemsState(myDocsResponse.items, myDocumentsListName);
          const results = modified(modifiedmyDocs);
          const tagged =
            searchQuery.length > 0
              ? results.map((d) => ({
                  ...d,
                  searchMeta: d.searchMeta ?? {
                    foundBy: "title" as const,
                    highlightedText: "",
                    elasticsearchId: "",
                    elasticsearchScore: 0,
                    DocumentFileId: null,
                  },
                }))
              : results;

          setDocuments(tagged);
        } else {
          const myDocsResponse = nextPage
            ? await getItemsRequest({
                listName: myDocumentsListName,
                filters: allFilters,
                sortOrder: { name: "Created", desc: true },
                rowLimit: 10,
                nextPage: nextPage,
              })
            : { items: [] as any[], nextPage: null };

          if (cancelled) return;
          setNextPage(myDocsResponse.nextPage);

          const modifiedmyDocs = modifiedItemsState(myDocsResponse.items, myDocumentsListName);
          const results = modified(modifiedmyDocs);
          const tagged =
            searchQuery.length > 0
              ? results.map((d) => ({
                  ...d,
                  searchMeta: d.searchMeta ?? {
                    foundBy: "title" as const,
                    highlightedText: "",
                    elasticsearchId: "",
                    elasticsearchScore: 0,
                    DocumentFileId: null,
                  },
                }))
              : results;
          setDocuments((prev) => [...prev, ...tagged]);
        }
      } catch (error) {
        console.error("ошибка подгрузки документов");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [dispatch, activeFilterType, JSON.stringify(filters), JSON.stringify(folders), page, searchQuery, searchByPartialMatch, searchByContent, filtersReady]);

  useEffect(() => {
    if (documents.length) {
      const documentsResponsibleEmails = documents.map((document) => document.responsibleForRevision.email.trim());

      dispatch(addUsers(documentsResponsibleEmails));
    }
  }, [documents]);

  const hasNextPage = !!nextPage || hasMoreEsTitle || hasMoreEsText;

  return (
    <>
      <FlexRow className="pt-6">
        <FlexCol className="w-full">
          <FlexCol className="mb-5 gap-4">
            <FlexRow className="grow gap-[10px]">
              <SearchUI
                placeholder={
                  searchByPartialMatch || searchByContent
                    ? "Найти документ по частичному совпадению названия или кода документа"
                    : "Найти документ по названию или коду документа"
                }
                defaultValue={searchQuery}
                onClick={handleSearch}
              />
              <FlexCol
                onClick={() => toggleFilter("folder")}
                className={twMerge(
                  activeFilterType === "folder" ? "text-darkblue" : "",
                  "items-center justify-center h-full",
                )}
              >
                <FlexCol
                  className={twMerge(
                    "w-[46px] h-[46px] bg-white rounded-md justify-center items-center cursor-pointer",
                    hasNonEmptyFolders ? "bg-[#012169] text-[#FFFFFF]" : "",
                  )}
                >
                  <FolderIcon className="w-6 h-6" />
                </FlexCol>
              </FlexCol>
              <FlexCol
                onClick={() => toggleFilter("filter")}
                className={twMerge(
                  activeFilterType === "filter" ? "text-darkblue" : "",
                  "items-center justify-center h-full",
                )}
              >
                <FlexCol
                  className={twMerge(
                    "w-[46px] h-[46px] bg-white rounded-md justify-center items-center cursor-pointer",
                    hasNonEmptyArray ? "bg-[#012169] text-[#FFFFFF]" : "",
                  )}
                >
                  <AdjustmentsHorizontalIcon className="w-4 h-4" />
                </FlexCol>
              </FlexCol>
            </FlexRow>
            <FlexCol className="items-start gap-3">
              <label className="flex gap-x-2 items-center cursor-pointer select-none">
                <CustomCheckbox checked={searchByPartialMatch} onChange={handleToggleSearchByPartialMatch}>
                  <span className="text-sm text-[#637381]">Искать по частичному совпадению</span>
                </CustomCheckbox>
              </label>
              <label className="flex gap-x-2 items-center cursor-pointer select-none">
                <CustomCheckbox checked={searchByContent} onChange={handleToggleSearchByContent}>
                  <span className="text-sm text-[#637381]">Искать по тексту документа</span>
                </CustomCheckbox>
              </label>
              {searchQuery.length > 0 && <span className="text-sm text-[#637381]">Найдено: {documents.length}</span>}
            </FlexCol>
          </FlexCol>
          <TableWithFilters documents={documents} cb={cb} loading={loading} />
          {hasNextPage && !loading && (
            <TGButton onClick={() => setPage((prev) => prev + 1)} color="bluedark">
              Подгрузить еще документы
            </TGButton>
          )}
        </FlexCol>
      </FlexRow>

      {createPortal(
        <FavoriteFiltersModal
          closeModal={() => setFavoriteFilterModalOpened(false)}
          opened={favoriteFilterModalOpened}
          saveFilter={saveFilter}
        />,
        document.body,
      )}
      <DialogComponent open={isSaved} toggleDialog={closeDialog}>
        <FlexCol className="w-[600px] shrink">
          <FlexRow className="mb-9">
            <h3 className="text-black">Назовите набор фильтров</h3>
          </FlexRow>
          <FlexRow className="mb-9">
            <InputFilterSet initial={filterTitle} onChange={getSetTitle} />
          </FlexRow>
          <FlexRow className="justify-between px-10 pt-10 -mx-10 border-t">
            <FlexCol>
              <TGButton onClick={closeDialog}>Отмена</TGButton>
            </FlexCol>
            <FlexCol>
              <TGButton color="bluedark" icon={<SaveIcon />} onClick={updateSetFilter}>
                Сохранить
              </TGButton>
            </FlexCol>
          </FlexRow>
        </FlexCol>
      </DialogComponent>
    </>
  );
}

export default MyDocumentPage;
